import { Table } from "react-bootstrap";
import TopImage from "../admin/common/TopImage";
import BackButton from "./BackButton";
import { Images } from "../../config/images";
import "./RolesPermissions.css";

/**
 * @typedef {Object} Permission
 * @property {string} feature - Feature/action name
 * @property {Object<string, boolean>} roles - Role permissions map
 */

/**
 * @typedef {Object} RolesPermissionsProps
 * @property {string[]} [roles] - Array of role names
 * @property {Permission[]} [permissions] - Array of permissions data
 * @property {string} [title] - Page title (default: "PERMISSIONS")
 * @property {string} [fallbackPath] - Back button fallback path
 */

// Default dummy data
const defaultRoles = [
    "Super Admin",
    "Admin",
    "Principal",
    "Assistant",
    "Accounts",
    "Viewer",
];

const defaultPermissions = [
    {
        feature: "Manage Users",
        roles: {
            "Super Admin": true,
            Admin: false,
            Principal: false,
            Assistant: false,
            Accounts: true,
            Viewer: false,
        },
    },
    {
        feature: "View Billing / Subscription",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: false,
            Assistant: false,
            Accounts: true,
            Viewer: false,
        },
    },
    {
        feature: "Edit Billing",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: false,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "Manage Data",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "Upload / Edit Content",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: true,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "View Reports",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: true,
            Accounts: false,
            Viewer: true,
        },
    },
    {
        feature: "Edit Reports",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: false,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "Track Payments",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "Assign Students",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "Assign Teachers",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
    {
        feature: "Manage Roles & Permissions",
        roles: {
            "Super Admin": true,
            Admin: true,
            Principal: true,
            Assistant: false,
            Accounts: false,
            Viewer: false,
        },
    },
];

/**
 * A reusable Roles & Permissions component that displays a permission matrix.
 */
const RolesPermissions = ({
    roles = defaultRoles,
    permissions = defaultPermissions,
    title = "PERMISSIONS",
    fallbackPath = "/",
}) => {
    const renderPermissionIcon = (hasPermission) => {
        if (hasPermission) {
            return (
                <i
                    className="bi bi-check-circle-fill"
                    style={{ color: "#28a745", fontSize: "20px" }}
                ></i>
            );
        }
        return (
            <i
                className="bi bi-x-circle-fill"
                style={{ color: "#dc3545", fontSize: "20px" }}
            ></i>
        );
    };

    return (
        <div>
            <div className="pb-2">
                <BackButton fallbackPath={fallbackPath} />
            </div>
            <TopImage imageSrc={Images.TopImage} text={title} />

            <div className="custom-table-container mt-4">
                <div className="custom-table-wrapper">
                    <Table hover className="custom-table permissions-table">
                        <thead className="custom-table-head">
                            <tr>
                                <th className="">Feature / Role</th>
                                {roles.map((role) => (
                                    <th key={role} className="text-center">
                                        {role}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {permissions.length > 0 ? (
                                permissions.map((permission, index) => (
                                    <tr key={index}>
                                        <td className="">{permission.feature}</td>
                                        {roles.map((role) => (
                                            <td key={role} className="text-center">
                                                {renderPermissionIcon(permission.roles[role])}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={roles.length + 1}>
                                        <div className="text-center m-5">
                                            No permissions data available.
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default RolesPermissions;
